import {
  GridColDef,
  GridColumnGroupingModel,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../components/datagrid/AppDataGrid";
import { TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { common, input, reduce } from "../../../i18n";
import alertService from "../../../components/alert/alertService";
import { ApiError } from "../../../api/ApiError";
import { goalSettingService } from "../../../service/goal-setting.service";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { GoalSettingContext } from "../../../contexts/GoalSettingContext";
import { GoalPlanBaseYear } from "../../../models/GoalSetting";
import { useFormContext, useWatch } from "react-hook-form";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { tableStyle } from "./AddGoalTableStyle";
import { NumericFormat } from "react-number-format";

interface BaseYearFormValues {
  baseYear: number | null;
  startYear: number | null;
  targetYear: number | null;
  baseYearEmission: number;
  goalNetEmission: number;
  percentReduction: number;
  targetAbsoluteRate: boolean;
  rows: GoalPlanBaseYear[];
}

export const BaseYearSettingTable = () => {
  const { t } = useTranslation([common, input, reduce], { nsMode: "fallback" });
  const {
    goalInfo,
    methodology,
    isEditMode,
    hasUserTriggered,
    setHasUserTriggered,
  } = GoalSettingContext();

  const { watch, setValue, getValues, reset, control, formState } =
    useFormContext<BaseYearFormValues>();

  const baseYear = watch("baseYear");
  const startYear = watch("startYear");
  const targetYear = watch("targetYear");
  const baseYearEmission = watch("baseYearEmission");
  const percentReduction = watch("percentReduction");
  const targetAbsoluteRate = watch("targetAbsoluteRate");
  const rows = watch("rows");

  const [rowsLocal, setRowsLocal] = useState<GoalPlanBaseYear[]>([]);

  const isRowsDirty = !!formState.dirtyFields?.rows;

  // // preload edit mode [Trigger Failed Useless]
  // useEffect(() => {
  //   if(!window.__RHF_BASEYEAR__?.formState?.isDirty){
  //     setValue("rows", rows);
  //     setRowsLocal(rows)
  //   }else{
  //     if (goalInfo && methodology === "baseYear") {
  //       const baseYearPlans = Array.isArray(goalInfo.plans)
  //         ? (goalInfo.plans as GoalPlanBaseYear[])
  //         : [];

  //       reset({
  //         baseYear: goalInfo.baseYear,
  //         startYear: goalInfo.startYear,
  //         targetYear: goalInfo.targetYear,
  //         baseYearEmission: goalInfo.baseYearEmission,
  //         percentReduction: goalInfo.percentReduction,
  //         targetAbsoluteRate: goalInfo.targetAbsoluteRate,
  //         rows: baseYearPlans,
  //       }, {keepDefaultValues: false, keepErrors: false});

  //       setRowsLocal(baseYearPlans);
  //       setHasUserTriggered(false);
  //     }
  //   }

  // }, [goalInfo, methodology]);

  // Regenerate plans when years or mode change
  useEffect(() => {
    // console.log("setRow123", rows)
    const handleRegeneratePlans = async () => {
      if (window.__RHF_BASEYEAR__?.formState?.isDirty === false) {
        const rows = watch("rows");
        setRowsLocal(rows);
        return;
      }

      if (isEditMode && !hasUserTriggered) return;
      if (!baseYear || !startYear || !targetYear) return;
      if (startYear >= targetYear) return;

      // Manual Mode
      if (!targetAbsoluteRate) {
        const allYears: number[] = [];
        for (let y = startYear; y <= targetYear; y++) {
          allYears.push(y);
        }

        const currentRows = [...rowsLocal];

        // 🟩 เพิ่มปีใหม่ที่ยังไม่มี
        const addedRows = allYears
          .filter((y) => !currentRows.some((r) => r.year === y))
          .map((y) => ({
            year: y,
            // baseYearEmission:
            //   currentRows.find((r) => r.year === y)?.baseYearEmission ??
            //   baseYearEmission,
            baseYearEmission: baseYearEmission,
            percentReduction: 0,
            targetReduction: 0,
            targetEmission: 0,
          }));

        // 🟥 ตัดปีที่อยู่นอกช่วง
        const validRows = currentRows
          .filter((r) => r.year >= startYear && r.year <= targetYear)
          .concat(addedRows)
          .sort((a, b) => a.year - b.year);

        setRowsLocal(validRows);
        setValue("rows", validRows);

        // 🔁 regenerate ผ่าน API
        try {
          const payload = {
            baseYear,
            startYear,
            targetYear,
            percentReduction,
            targetAbsoluteRate: targetAbsoluteRate,
            baseYearEmission,
            plans: validRows.map((r) => ({
              year: r.year,
              percentReduction: r.percentReduction ?? 0,
            })),
            // plans: validRows
          };

          alertService.showLoading();
          // debugger
          const result = await goalSettingService.generatePlanBaseyear(payload);
          alertService.closeLoading();

          if (result?.plans?.length) {
            const cleanPlans = result.plans.map((p) => ({
              ...p,
              percentReduction:
                p.percentReduction === null ? 0 : p.percentReduction,
            }));
            setRowsLocal(cleanPlans);
            setValue("rows", cleanPlans);
            setValue("goalNetEmission", result.goalNetEmission || 0);
          }
        } catch (err) {
          alertService.closeLoading();
          console.error("Error regenerating manual plans:", err);
        }
        return;
      }

      // Auto Mode
      try {
        const payload = {
          baseYear,
          startYear,
          targetYear,
          percentReduction,
          targetAbsoluteRate: targetAbsoluteRate,
          baseYearEmission,
        };

        alertService.showLoading();
        const result = await goalSettingService.generatePlanBaseyear(payload);
        alertService.closeLoading();

        if (result?.plans?.length) {
          setRowsLocal(result.plans);
          setValue("rows", result.plans);
          setValue("goalNetEmission", result.goalNetEmission || 0);
        }
      } catch (err) {
        alertService.closeLoading();
        console.error("Error auto generate plan:", err);
      }
    };

    handleRegeneratePlans();
  }, [
    targetAbsoluteRate,
    // percentReduction,
    baseYearEmission,
    baseYear,
    startYear,
    targetYear,
    hasUserTriggered,
  ]);

  // แก้ไขค่า reduction ทีละแถว (manual mode)
  const handleUpdateReduction = async (year: number, value: number) => {
    setHasUserTriggered(true);
    try {
      const current = getValues();
      const filteredPlans = rowsLocal
        .filter(
          (p) =>
            p.year >= (current.startYear ?? 0) &&
            p.year <= (current.targetYear ?? 9999),
        )
        .map((p) => ({
          year: p.year,
          percentReduction:
            p.year === year ? value : Number(p.percentReduction ?? 0),
        }));

      const payload = {
        baseYear,
        startYear,
        targetYear,
        percentReduction,
        targetAbsoluteRate: targetAbsoluteRate,
        baseYearEmission,
        plans: filteredPlans,
      };

      alertService.showLoading();
      const result = await goalSettingService.generatePlanBaseyear(payload);
      alertService.closeLoading();

      if (result?.plans?.length) {
        // const validPlans = result.plans.filter(
        //   (p) => p.percentReduction !== null && p.percentReduction !== undefined
        // );
        setRowsLocal(result?.plans);
        setValue("rows", result?.plans);
        setValue("goalNetEmission", result.goalNetEmission || 0);
        alertService.mixin(t("Updated-Success"));
      }
    } catch (err) {
      alertService.closeLoading();
      console.error("Error updating reduction:", err);
      if (!(err instanceof ApiError)) {
        await alertService.error(t("Error-Updating-Plan"));
      }
    }
  };

  const columnGroup: GridColumnGroupingModel = [
    {
      groupId: t("Year"),
      children: [{ field: "be" }, { field: "ad" }],
      headerAlign: "center",
    },
  ];
  // Columns
  const columns: GridColDef<GoalPlanBaseYear>[] = [
    {
      field: "be",
      headerName: t("BE"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<GoalPlanBaseYear>) => {
        const beYear = params.row.year ? params.row.year + 543 : "";
        return (
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
            }}
          >
            {beYear}
          </Typography>
        );
      },
    },
    {
      field: "ad",
      headerName: t("AD"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<GoalPlanBaseYear>) => {
        const adYear = params.row.year;
        return (
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
            }}
          >
            {adYear}
          </Typography>
        );
      },
    },
    {
      field: "baseYearEmission",
      // headerName: `${t("Base-Year-Emission")} (tCO₂e)`,
      renderHeader: () => (
        <Typography variant="h4">
          {t("Base-Year-Emission")} (tCO<sub>2</sub>e)
        </Typography>
      ),
      minWidth: 220,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
    {
      field: "percentReduction",
      headerName: `% ${t("Reduction")}`,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const year = params.row.year;
        const inRange =
          startYear !== null &&
          targetYear !== null &&
          year >= startYear &&
          year <= targetYear;

        // ถ้าอยู่นอกช่วง → ไม่ต้อง render input
        if (!inRange) {
          return (
            <Typography
              variant="body2"
              sx={{
                textAlign: "center",
                width: "100%",
              }}
            ></Typography>
          );
        }

        // ✅ อัปเดตค่าใน state ตอนพิมพ์
        const handleChangeLocal = (values: any) => {
          setHasUserTriggered(true);
          const { floatValue } = values;
          setRowsLocal((prev) =>
            prev.map((row) =>
              row.year === params.row.year
                ? { ...row, percentReduction: floatValue ?? null }
                : row,
            ),
          );
        };

        // ✅ ยิง API ตอน blur ถ้าค่าถูกต้อง
        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
          const raw = e.target.value.trim();

          // ✅ ถ้าไม่มีค่าเลย หรือเป็นช่องว่าง
          if (raw === "") {
            alertService.error(t("only-numbers-are-allowed")); // 🔔 แจ้งเตือน
            setRowsLocal((prev) =>
              prev.map((row) =>
                row.year === params.row.year
                  ? { ...row, percentReduction: 0 }
                  : row,
              ),
            );
            return;
          }

          const val = parseFloat(raw);

          // ✅ ถ้าไม่ใช่ตัวเลข
          if (isNaN(val)) {
            alertService.error(t("only-numbers-are-allowed"));
            setRowsLocal((prev) =>
              prev.map((row) =>
                row.year === params.row.year
                  ? { ...row, percentReduction: null }
                  : row,
              ),
            );
            return;
          }

          // ✅ ถ้าอยู่นอกช่วง 0–100
          if (val < 0 || val > 100) {
            alertService.error(t("value-must-be-between-0-and-100"));
            setRowsLocal((prev) =>
              prev.map((row) =>
                row.year === params.row.year
                  ? { ...row, percentReduction: null }
                  : row,
              ),
            );
            return;
          }

          // ✅ ผ่านทุกเงื่อนไข → ยิง API ได้
          if (!targetAbsoluteRate) {
            handleUpdateReduction(params.row.year, val);
          }
        };

        return (
          <NumericFormat
            value={params.row.percentReduction ?? ""}
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            allowLeadingZeros={false}
            customInput={TextField}
            size="small"
            disabled={targetAbsoluteRate}
            sx={{ width: "100%" }}
            inputProps={{
              style: { textAlign: "right" },
              min: 0,
              max: 100,
              step: 1,
            }}
            // ✅ ไม่ให้พิมพ์เลขเกิน 100 หรือติดลบ
            isAllowed={(values) => {
              const { floatValue } = values;
              return (
                floatValue == null || (floatValue >= 0 && floatValue <= 100)
              );
            }}
            onValueChange={handleChangeLocal}
            onBlur={handleBlur}
            onKeyDown={(event) => event.stopPropagation()}
            onKeyUp={async (event: React.KeyboardEvent) => {
              switch (event.key) {
                case "Enter":
                  (document.activeElement as HTMLElement).blur();
                  break;
                default:
                  break;
              }
            }}
          />
        );
      },
    },
    {
      field: "targetEmission",
      // headerName: `${t("Emission-Reduction")} (tCO₂e)`,
      renderHeader: () => (
        <Typography variant="h4">
          {t("Emission-Reduction")} (tCO<sub>2</sub>e)
        </Typography>
      ),
      flex: 1,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
    {
      field: "targetReduction",
      // headerName: `${t("Target-Reduction")} (tCO₂e)`,
      renderHeader: () => (
        <Typography variant="h4">
          {t("Target-Reduction")} (tCO<sub>2</sub>e)
        </Typography>
      ),
      flex: 1,
      align: "right",
      headerAlign: "center",
      valueFormatter: (value) =>
        value === null ? "" : NumberFormatterUtils.numberFormat(value),
    },
  ];

  // ✅ sync ค่า rows จาก form ทุกครั้งที่ reset หรือค่าภายใน form เปลี่ยน
  useEffect(() => {
    const form = window.__RHF_BASEYEAR__;
    if (!form) return;

    // 🔹 ดึงค่า rows ปัจจุบันจาก RHF form
    const currentRows = form.getValues("rows") ?? [];
    setRowsLocal(currentRows);

    // 🔹 subscribe เฉพาะเวลาค่า rows ใน form เปลี่ยน (รวมถึงตอน reset)
    const subscription = form.watch((value: any, { name }: any) => {
      // กรณี reset จะไม่มี name → ต้อง sync ทั้งหมด
      if (!name || name === "rows") {
        const latestRows = form.getValues("rows") ?? [];
        setRowsLocal(latestRows);
      }
    });

    return () => subscription?.unsubscribe?.();
  }, []);

  return (
    <>
      {/* {JSON.stringify(rowsLocal)} */}
      <AppDataGrid
        rows={rowsLocal}
        columnGroupingModel={columnGroup}
        columns={columns}
        getRowId={(r) => r.year}
        sx={tableStyle()}
        autoHeight
        hideFooter
      />
    </>
  );
};
